export interface ProgramWeek {
  key: string;
  title: string;
  topics: string[];
  miniProjects?: string[];
  capstoneProject?: { title: string; features: string[] };
}

export interface ProgramHighlightModule {
  module: string;
  topics: string[];
}

export interface ParsedTopic {
  title: string;
  description?: string;
}

export interface ProgramProject {
  title: string;
  stack: string;
  focus: string;
}

export interface LearningJourneyStep {
  title: string;
  description?: string;
}

export interface ParsedProgramContent {
  title?: string;
  duration?: string;
  mode?: string;
  certification?: string;
  highlights: ProgramHighlightModule[];
  outcomes: string[];
  projects: ProgramProject[];
  learningJourney: LearningJourneyStep[];
  weeks: ProgramWeek[];
  evaluation: string[];
  careerSupport: string[];
}

export function parseTopicLine(topic: string): ParsedTopic {
  const separator = topic.indexOf(' - ');
  if (separator === -1) return { title: topic };
  return {
    title: topic.slice(0, separator),
    description: topic.slice(separator + 3),
  };
}

function parseProgramHighlights(raw: unknown): ProgramHighlightModule[] {
  if (!Array.isArray(raw) || raw.length === 0) return [];

  if (typeof raw[0] === 'string') {
    return [{ module: 'Program Highlights', topics: raw as string[] }];
  }

  return raw
    .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
    .map((item) => ({
      module:
        typeof item.module === 'string'
          ? item.module.replace(/^\d+\.\s*/, '').trim()
          : 'Module',
      topics: Array.isArray(item.topics)
        ? item.topics.filter((t): t is string => typeof t === 'string')
        : [],
    }))
    .filter((m) => m.topics.length > 0);
}

export function safeJsonParse<T>(value: string | null | undefined, fallback: T): T {
  if (!value || typeof value !== 'string') return fallback;
  try {
    const cleaned = value.replace(/\\n/g, '\n').replace(/\\"/g, '"').replace(/\\\\/g, '\\');
    return JSON.parse(cleaned) as T;
  } catch {
    return fallback;
  }
}

export function parseProgramContent(content: string | null | undefined): ParsedProgramContent | null {
  const root = safeJsonParse<{ program?: Record<string, unknown> }>(content, {});
  const program = root?.program;
  if (!program || typeof program !== 'object') return null;

  const curriculum = program.curriculum as
    | Record<string, ProgramWeek & { capstoneProject?: { title: string; features: string[] } }>
    | undefined;
  const weeks: ProgramWeek[] = [];

  if (curriculum && typeof curriculum === 'object') {
    Object.entries(curriculum).forEach(([key, week]) => {
      if (!week || typeof week !== 'object') return;
      weeks.push({
        key,
        title: week.title || key,
        topics: Array.isArray(week.topics) ? week.topics : [],
        miniProjects: Array.isArray(week.miniProjects) ? week.miniProjects : undefined,
        capstoneProject: week.capstoneProject,
      });
    });
  }

  const evaluation = program.evaluation as { methodology?: string[] } | undefined;
  const rawOutcomes = Array.isArray(program.outcomes)
    ? program.outcomes
    : Array.isArray(program.programOutcomes)
      ? program.programOutcomes
      : [];

  const projects: ProgramProject[] = Array.isArray(program.projects)
    ? program.projects
        .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
        .map((item) => ({
          title: typeof item.title === 'string' ? item.title : '',
          stack: typeof item.stack === 'string' ? item.stack : '',
          focus: typeof item.focus === 'string' ? item.focus : '',
        }))
        .filter((item) => item.title)
    : [];

  const learningJourney: LearningJourneyStep[] = Array.isArray(program.learningJourney)
    ? program.learningJourney
        .filter((item): item is string => typeof item === 'string')
        .map((item) => parseTopicLine(item))
    : [];

  return {
    title: program.title as string | undefined,
    duration: program.duration as string | undefined,
    mode: program.mode as string | undefined,
    certification: program.certification as string | undefined,
    highlights: parseProgramHighlights(program.programHighlights),
    outcomes: rawOutcomes.filter((item): item is string => typeof item === 'string'),
    projects,
    learningJourney,
    weeks,
    evaluation: Array.isArray(evaluation?.methodology) ? evaluation.methodology : [],
    careerSupport: Array.isArray(program.careerSupport) ? (program.careerSupport as string[]) : [],
  };
}

/** Course materials JSON array, or a comma-separated tools string. */
export function parseStringList(value: string | null | undefined): string[] {
  if (!value || typeof value !== 'string') return [];
  const asJson = safeJsonParse<unknown>(value, null);
  if (Array.isArray(asJson)) {
    return asJson.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
  }
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}
